import { useState, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftAvatar from "components/SoftAvatar";
import DashboardNavbar from "cxfcomponents/Navbars/DashboardNavbar";
import { useAuth0 } from "@auth0/auth0-react";
import UploadBadge from "./components/uploadBadge";
import UploadModal from "./components/UploadModal";
import PictureMenu from "./components/PictureMenu";
import RemovePictureModal from "./components/RemovePictureModal";
import DefaultUserAvatar from "./components/DefaultUserAvatar";
import { TenantContext } from "context/AppProvider/TenantContext";
import { UserContext } from "context/AppProvider/UserContext";
import "./style.css";
import { useDispatch } from "react-redux";
import { UPDATE_USER } from "redux/constants/portal";

function Header({ isOwner, hasNoBannerAndAvatar, forUserSettings }) {
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [openRemovePictureModal, setOpenRemovePictureModal] = useState(false);
  const [pictureMenuAnchorEl, setPictureMenuAnchorEl] = useState(null);
  const [pictureMenuHasImage, setPictureMenuHasImage] = useState(false);
  const [logoKey, setLogoKey] = useState(null);
  const { pathname } = useLocation();
  const { user } = useAuth0();
  const [userAvatar, setUserAvatar] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const { tenantData, tenantLogoKey } = useContext(TenantContext);
  const { userData } = useContext(UserContext);
  const dispatch = useDispatch();

  useEffect(() => {
    if (userData?.image && userData.image.trim()) {
      setUserAvatar(userData.image);
    } else {
      setUserAvatar("");
    }
  }, [userData]);

  useEffect(() => {
    if (tenantData?.logo_url && tenantData.logo_url.trim()) {
      setCompanyLogo(tenantData.logo_url);
    } else {
      setCompanyLogo("");
    }
  }, [tenantData, logoKey, tenantLogoKey]);

  const renderName = () => {
    if (pathname === "/administration/company") {
      if (tenantData && tenantData.company_name) {
        return tenantData.company_name;
      }
      return "";
    } else if (
      pathname === "/home" ||
      pathname === "/home/profile" ||
      pathname === "/privacy-and-security" ||
      pathname === "/home/settings"
    ) {
      if (userData && (userData.first_name || userData.last_name)) {
        return `${userData.first_name} ${userData.last_name}`;
      } else {
        return "";
      }
    } else {
      return "";
    }
  };

  const renderLocation = () => {
    if (pathname === "/administration/company") {
      if (tenantData && tenantData.city && tenantData.state) {
        return `${
          tenantData?.city && tenantData?.city?.toLowerCase() !== "null"
            ? `${tenantData?.city},`
            : ""
        } ${tenantData.state}`;
      } else {
        return "";
      }
    } else {
      return "";
    }
  };

  const handleSetUserAvatar = (newAvatar) => {
    setUserAvatar(newAvatar);
    localStorage.setItem("realtimeAvatar", newAvatar ? newAvatar : "");
    dispatch({
      type: UPDATE_USER,
      payload: {
        image: newAvatar
      }
    });
  };

  const handleOpenPictureMenu = (event) => {
    event.stopPropagation();
    const hasImage =
      pathname === "/administration/company"
        ? !!companyLogo?.trim()
        : !!userAvatar?.trim();

    setPictureMenuAnchorEl(event.currentTarget);
    setPictureMenuHasImage(hasImage);
  };

  return (
    <SoftBox position="relative">
      {!forUserSettings && <DashboardNavbar avatar={userAvatar} />}

      {!hasNoBannerAndAvatar ? (
        <SoftBox my={1}>
          <Card
            sx={{
              backdropFilter: `saturate(200%) blur(30px)`,
              backgroundColor: ({ functions: { rgba }, palette: { white } }) =>
                rgba(white.main, 0.8),
              mx: 0.5,
              py: 2,
              px: 2,
              boxShadow: 0,
            }}
          >
            <Grid
              container
              alignItems="center"
              sx={{
                justifyContent: {
                  xs: "center",
                  sm: "space-between"
                },
                gap: 2
              }}
            >
              <Grid>
                <Grid container spacing={5} alignItems="center">
                  <Grid>
                    <UploadBadge onClick={handleOpenPictureMenu} pathname={pathname} isOwner={isOwner}>
                      {pathname === "/administration/company" && (
                        <SoftAvatar
                          key={tenantLogoKey}
                          src={companyLogo}
                          alt="company-image"
                          variant="rounded"
                          size="xl"
                          shadow="sm"
                        />
                      )}
                      {pathname !== "/administration/company" ? (
                        userAvatar && userAvatar.trim() ? (
                          <SoftAvatar
                            key="user-image"
                            src={userAvatar}
                            alt="user-image"
                            variant="rounded"
                            size="xxl"
                            shadow="sm"
                            sx={{ border: '0.5px solid #e6e6e6 !important', mt: 3}}
                          />
                        ) : (
                          <SoftAvatar alt="user-image" variant="rounded" size="xxl" shadow="sm" sx={{ mt: 3 }}>
                            <DefaultUserAvatar
                              firstName={userData?.first_name}
                              lastName={userData?.last_name}
                              size="xxl"
                              border="unset"
                              variant="rounded"
                              disableFocusListener={false}
                              disableTouchListener={false}
                              open={false}
                            />
                          </SoftAvatar>
                        )
                      ) : null}
                    </UploadBadge>

                    <PictureMenu
                      pictureMenuAnchorEl={pictureMenuAnchorEl}
                      setPictureMenuAnchorEl={setPictureMenuAnchorEl}
                      setOpenUploadModal={setOpenUploadModal}
                      setOpenRemovePictureModal={setOpenRemovePictureModal}
                      hasImage={pictureMenuHasImage}      
                    />
                  </Grid>

                  <Grid>
                    <SoftBox height="100%" mt={5} lineHeight={1}>
                      <SoftTypography
                        variant="h3"
                        fontWeight="light"
                        sx={{
                          maxWidth: "400px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}
                        title={renderName()}
                      >
                        {renderName()}
                      </SoftTypography>
                      <SoftTypography variant="button" color="text" fontWeight="light" fontSize="16px">
                        {forUserSettings ? user?.email : renderLocation()}
                      </SoftTypography>
                    </SoftBox>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Card>
        </SoftBox>
      ) : null}

      <UploadModal
        openUploadModal={openUploadModal}
        setOpenUploadModal={setOpenUploadModal}
        pathname={pathname}
        setUserAvatar={handleSetUserAvatar}
        setCompanyLogo={setCompanyLogo}
        setLogoKey={setLogoKey}
        targetUser={userData}
        targetTenant={tenantData}
      />

      <RemovePictureModal
        openRemovePictureModal={openRemovePictureModal}
        setOpenRemovePictureModal={setOpenRemovePictureModal}
        setUserAvatar={handleSetUserAvatar}
        setCompanyLogo={setCompanyLogo}
        setLogoKey={setLogoKey}
        targetUser={userData}
        targetTenant={tenantData}
      />
    </SoftBox>
  );
}

export default Header;
